import * as _ from 'lodash';
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { colors, media } from 'consumer-components';

import TableCell from './TableCell';

interface Heading {
  text: string;
}

interface Row {
  cells: Cell[];
}

interface Cell {
  text: string;
}

// holds all the data to be displayed in the table itself
export interface TableConfig {
  headings: Heading[];
  rows: Row[];
}

export interface SimpleDataTableProps {
  caption: string; // for a11y, a caption should describe the content of the table
  emptyStatus?: React.ReactNode;
  errorStatus?: React.ReactNode;
  loadingStatus?: React.ReactNode;
  tableConfig: TableConfig;
}

const styles = StyleSheet.create({
  simpleDataTable: {
    position: 'relative',
  },
  showingScreen: {
    minHeight: 280,
  },
  statusScreen: {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    position: 'absolute',
  },
  table: {
    width: '100%',
    [media.mobileMedium]: {
      tableLayout: 'fixed',
    },
  },
  headingRow: {
    borderBottom: `2px solid ${colors.clayMinus3}`,
  },
  row: {
    borderBottom: `1px solid ${colors.clayMinus3}`,
  },
  // Allow caption to be readable by screen readers but hidden otherwise.
  // WARNING: Changes to this class should be tested with VoiceOver or equivalent a11y tools. Absolute position in the
  // a11y.srOnly class breaks screen reader functionality. Any styling that removes caption from natural DOM flow like
  // float or absolute position will cause the screen reader to skip the last row of the table.
  hiddenCaption: {
    border: 0,
    height: 0,
    margin: 0,
    overflow: 'hidden',
    padding: 0,
  },
});

export default class SimpleDataTable extends React.PureComponent<SimpleDataTableProps> {
  renderHeadings = (headings: Heading[]) => (
    <tr key="heading" className={css(styles.headingRow)}>
      {headings.map((heading, headingIndex) => (
        <TableCell
          key={`simpleDataTable-heading-${headingIndex}`}
          content={heading.text}
          heading={true}
          scope="col"
          textAlign={headingIndex === 0 ? 'left' : 'right'}
        />
      ))}
    </tr>
  );

  renderRows = (rows: Row[]) =>
    rows.map((row, rowIndex) => (
      <tr key={`row-${rowIndex}`} className={css(styles.row)}>
        {row.cells.map((cell, cellIndex) => (
          <TableCell
            key={`${rowIndex}-${cellIndex}`}
            content={cell.text}
            textAlign={cellIndex === 0 ? 'left' : 'right'}
          />
        ))}
      </tr>
    ));

  renderStatusScreen = (screen: React.ReactNode) => <div className={css(styles.statusScreen)}>{screen}</div>;

  isEmpty = () => {
    const { tableConfig } = this.props;
    const { rows } = tableConfig;
    return _.isEmpty(rows);
  };

  render() {
    const { caption, emptyStatus, errorStatus, loadingStatus, tableConfig } = this.props;
    const { headings, rows } = tableConfig;
    const isEmpty = this.isEmpty();
    const status = errorStatus || loadingStatus || (isEmpty && emptyStatus);

    return (
      <div className={css(styles.simpleDataTable, status && styles.showingScreen)}>
        {status && this.renderStatusScreen(status)}
        <table aria-hidden={Boolean(status)} className={css(styles.table)}>
          <caption className={css(styles.hiddenCaption)}>{caption}</caption>
          <thead>{this.renderHeadings(headings)}</thead>
          <tbody>{this.renderRows(rows)}</tbody>
        </table>
      </div>
    );
  }
}
